// Deliverables: admin management (one deliverable per product) and the customer's "open item" call.
import apiClient from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getErrorMessage } from '../utils/apiError';

// GET /api/Deliverable lists every deliverable (admin); POST to the same URL creates or replaces a product's deliverable.
const DELIVERABLES_URL = API_ENDPOINTS.DELIVERABLES.CREATE;

/** Content type that gives the buyer collaborator access to a GitHub repository after payment. */
export const REPOSITORY_FILE_TYPE = 'repository';

export const DELIVERABLE_FILE_TYPES = [
  { value: REPOSITORY_FILE_TYPE, label: 'GitHub repository (grants access)' },
  { value: 'zip', label: 'ZIP download' },
  { value: 'pdf', label: 'PDF' },
  { value: 'video', label: 'Video' },
  { value: 'document', label: 'Document' },
  { value: 'code', label: 'Code download' },
  { value: 'other', label: 'Other' },
];

/**
 * { owner, repo } when the URL is a GitHub repository link (https://github.com/owner/repo, ".git" and
 * sub-paths allowed), otherwise null. Mirrors the server's check.
 */
export const parseGitHubRepository = url => {
  if (!url || typeof url !== 'string') return null;
  let value = url.trim();
  if (!value.includes('://')) value = `https://${value}`;
  try {
    const parsed = new URL(value);
    if (!/^(www\.)?github\.com$/i.test(parsed.hostname)) return null;
    const [owner, rawRepo] = parsed.pathname.split('/').filter(Boolean);
    const repo = rawRepo?.replace(/\.git$/i, '');
    return owner && repo ? { owner, repo } : null;
  } catch {
    return null;
  }
};

const isWebUrl = url => {
  try {
    const parsed = new URL(String(url).trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

/** Request body for POST /api/Deliverable. Keeps existing items' creation time; the server derives RepositoryName. */
const buildPayload = data => ({
  productId: data.productId,
  edition: data.edition?.trim() || 'Standard',
  deliverableContent: (data.deliverableContent || []).map(item => {
    const content = {
      title: item.title?.trim() || '',
      url: item.url?.trim() || '',
      fileType: item.fileType || '',
      description: item.description?.trim() || '',
      mimeType: item.mimeType?.trim() || '',
      fileSizeInBytes: Math.max(0, Math.round(Number(item.fileSizeInBytes) || 0)),
    };
    if (item.createdAt) content.createdAt = item.createdAt;
    if (item.accessDuration) content.accessDuration = item.accessDuration;
    return content;
  }),
  accessInstructions: data.accessInstructions?.trim() || '',
  licenseTerms: data.licenseTerms?.trim() || '',
  isActive: data.isActive !== false,
});

/**
 * Admin: every product's deliverable, most recently changed first.
 * GET /api/Deliverable -> [{ id, productId, productName, edition, deliverableContent: [...], accessInstructions, licenseTerms, isActive, createdAt, updatedAt }]
 */
export const getAllDeliverables = async () => {
  try {
    const response = await apiClient.get(DELIVERABLES_URL);
    const list = response.data?.data;
    return { success: true, data: Array.isArray(list) ? list : [] };
  } catch (error) {
    return {
      success: false,
      data: [],
      status: error.response?.status,
      error: getErrorMessage(error, "Couldn't load deliverables"),
    };
  }
};

/**
 * Admin: create or replace a product's deliverable (one per product).
 * POST /api/Deliverable { productId, edition, deliverableContent, accessInstructions, licenseTerms, isActive }
 */
export const saveDeliverable = async data => {
  try {
    const response = await apiClient.post(DELIVERABLES_URL, buildPayload(data));
    return {
      success: true,
      data: response.data?.data,
      message: response.data?.message || 'Deliverable saved',
    };
  } catch (error) {
    return { success: false, error: getErrorMessage(error, "Couldn't save the deliverable") };
  }
};

/** Older name for {@link saveDeliverable}. */
export const createDeliverable = saveDeliverable;

/**
 * Admin: products for the deliverable picker, hidden and inactive ones included.
 * GET /Products?IncludeHidden=true&ItemsPerPage=100 -> data.products
 */
export const getProductOptions = async () => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.PRODUCTS.GET_ALL, {
      params: { IncludeHidden: true, ItemsPerPage: 100 },
    });
    const products = response.data?.data?.products;
    return {
      success: true,
      data: (Array.isArray(products) ? products : [])
        .filter(p => p?.id)
        .map(p => ({ id: p.id, name: p.name || 'Untitled product', code: p.productId, isActive: p.isActive !== false }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  } catch (error) {
    return { success: false, data: [], error: getErrorMessage(error, "Couldn't load products") };
  }
};

/**
 * Get deliverables by order ID (paid orders only; the API answers 403 for unpaid ones)
 * @param {string} orderId - Order ID
 */
export const getDeliverablesByOrder = async orderId => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.DELIVERABLES.GET_BY_ORDER(orderId));
    return {
      success: true,
      data: response.data?.data || response.data || [],
    };
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error, 'Failed to fetch deliverables'),
      data: [],
    };
  }
};

/**
 * Admin: delete a product's deliverable. DELETE /api/Deliverable/product/{productId}
 * @param {string} productId - Product ID
 */
export const deleteDeliverableByProduct = async productId => {
  try {
    const response = await apiClient.delete(API_ENDPOINTS.DELIVERABLES.DELETE_BY_PRODUCT(productId));
    return {
      success: true,
      message: response.data?.message || 'Deliverable deleted',
    };
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error, "Couldn't delete the deliverable"),
    };
  }
};

/**
 * Admin "Provide access". For an order that isn't paid yet this MARKS IT AS PAID and runs the paid pipeline
 * (invoice, delivery, GitHub access, emails); for a paid order it re-runs delivery.
 * POST /api/Deliverable/manual-attach { orderId, productIds, grantGitHubAccess, gitHubUsername }
 */
export const provideOrderAccess = async ({ orderId, productIds, gitHubUsername }) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.DELIVERABLES.MANUAL_ATTACH, {
      orderId,
      productIds,
      grantGitHubAccess: true,
      gitHubUsername: gitHubUsername?.trim() || null,
    });
    return { success: true, data: response.data?.data, message: response.data?.data?.message || response.data?.message };
  } catch (error) {
    return { success: false, error: getErrorMessage(error, "Couldn't provide access") };
  }
};

/**
 * Track deliverable access
 * @param {Object} accessData - { orderId, productId }
 */
export const trackDeliverableAccess = async accessData => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.DELIVERABLES.TRACK_ACCESS, accessData);
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || 'Access tracked successfully',
    };
  } catch (error) {
    return {
      success: false,
      error: getErrorMessage(error, 'Failed to track access'),
    };
  }
};

/**
 * Validate deliverable data before submission (same rules as the server).
 * @returns {{ isValid: boolean, errors: string[] }}
 */
export const validateDeliverableData = data => {
  const errors = [];

  if (!data.productId || String(data.productId).trim().length === 0) {
    errors.push('Choose a product');
  }

  if (!Array.isArray(data.deliverableContent) || data.deliverableContent.length === 0) {
    errors.push('Add at least one item');
  } else {
    data.deliverableContent.forEach((item, index) => {
      const n = index + 1;
      if (!item.title?.trim()) errors.push(`Item ${n}: add a title`);
      if (!item.fileType) errors.push(`Item ${n}: choose a type`);
      if (!item.url?.trim()) {
        errors.push(`Item ${n}: add a link`);
      } else if (!isWebUrl(item.url)) {
        errors.push(`Item ${n}: enter a full link starting with https://`);
      } else if (item.fileType === REPOSITORY_FILE_TYPE && !parseGitHubRepository(item.url)) {
        errors.push(`Item ${n}: a GitHub repository link looks like https://github.com/owner/repository`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get the link for one deliverable item and open it. Order data from the API no longer contains
 * links; the server checks ownership, payment and access expiry, then returns the URL.
 * Call from a click handler so the new tab isn't blocked.
 * @param {string} orderId - Order ID (e.g. ORD-...)
 * @param {string} productId - Product the item belongs to
 * @param {number} itemIndex - Position of the item in that product's content list
 */
export const openDeliverableItem = async (orderId, productId, itemIndex) => {
  const tab = window.open('', '_blank');
  try {
    const response = await apiClient.post(API_ENDPOINTS.DELIVERABLES.OPEN(orderId), { productId, itemIndex });
    const url = response.data?.data?.url;
    if (!url) throw new Error('No link returned');
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.href = url;
    }
    return { success: true, url };
  } catch (error) {
    if (tab) tab.close();
    return {
      success: false,
      expired: error.response?.status === 410,
      error: error.response?.data?.message || 'Could not open this item',
    };
  }
};

const deliverableService = {
  getAllDeliverables,
  saveDeliverable,
  createDeliverable,
  getProductOptions,
  getDeliverablesByOrder,
  deleteDeliverableByProduct,
  provideOrderAccess,
  trackDeliverableAccess,
  openDeliverableItem,
  validateDeliverableData,
};

export default deliverableService;
